import type { DemoData } from "@/src/data/demos";

export interface DemoShellRecord {
  id: string;
  title: string;
  stage: string;
  owner: string;
  nextAction: string;
  signal: string;
}

export interface DemoShellAgent {
  name: string;
  role: string;
  status: "Active" | "Recommended" | "System";
  summary: string;
}

export interface DemoShellReceipt {
  id: string;
  title: string;
  detail: string;
  assistant: string;
  status: "Verified" | "Pending" | "Demo";
}

export function splitDemoStages(demo: DemoData): string[] {
  return demo.vocabulary.stages
    .split(/->|→/)
    .map((stage) => stage.trim())
    .filter(Boolean);
}

export function splitDemoCounterparties(demo: DemoData): string[] {
  return demo.vocabulary.worksWith
    .split(/,| and /)
    .map((party) => party.trim())
    .filter(Boolean);
}

export function singularDemoNoun(noun: string): string {
  const first = noun.split(",")[0]?.trim() || "record";
  if (first.endsWith("ies")) return `${first.slice(0, -3)}y`;
  if (first.endsWith("s") && !first.endsWith("ss")) return first.slice(0, -1);
  return first;
}

export function titleCase(value: string): string {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function demoSearchText(demo: DemoData): string {
  return `${demo.id} ${demo.name} ${demo.category} ${demo.vocabulary.tracks} ${demo.vocabulary.stages} ${demo.modules.join(" ")}`.toLowerCase();
}

function recordTitleExamplesForDemo(demo: DemoData, noun: string): string[] {
  const text = demoSearchText(demo);

  if (/football|academy|player|scout|squad/.test(text)) return ["U19 winger trial shortlist", "Left-back renewal watch", "Holding midfielder club match", "Scholarship promotion review", "Striker medical follow-up", "Sponsor fit shortlist"];
  if (/oil|gas|energy|well|hse|permit|downtime/.test(text)) return ["Well pad inspection risk", "Pump maintenance watch", "HSE evidence packet", "Permit renewal path", "Downtime exception", "Field handoff"];
  if (/property management|maintenance|lease|tenant/.test(text)) return ["Vacant Juffair unit", "AC maintenance ticket", "Lease renewal watch", "Owner report draft", "Service charge question", "Move-in inspection"];
  if (/\brealty\b|\breal estate\b|\blistings?\b|\bviewings?\b|\bbuyers?\b|\bsellers?\b|\bmortgage\b/.test(text)) return ["Seef off-plan buyer", "Amwaj viewing follow-up", "Seller valuation packet", "Mortgage condition file", "Investor shortlist", "Offer negotiation"];
  if (/social media|creator|content|campaign|creative|brand|design/.test(text)) return ["June campaign sprint", "Reels batch approval", "Brand deal deliverables", "UGC asset request", "Audience insight brief", "Creative refresh"];
  if (/trade|customs|shipment|docs|documents/.test(text)) return ["Commercial invoice set", "Customs hold packet", "Broker handoff", "Packing list review", "Export certificate", "Shipment clearance"];
  if (/manufacturing|quality|batch|defect|production/.test(text)) return ["Batch defect review", "Supplier deviation", "Inspection queue", "Corrective action plan", "Production hold", "Quality receipt"];
  if (/ecommerce|order|cart|sku|inventory/.test(text)) return ["High-margin SKU restock", "Delayed order cluster", "Refund risk queue", "Bundle performance watch", "Inventory gap", "Customer issue"];
  if (/recruit|candidate|interview|hiring/.test(text)) return ["Senior AE candidate loop", "Interview scorecard gap", "Offer approval packet", "Hiring manager follow-up", "Resume shortlist", "Reference check"];
  if (/field service|service ticket|dispatch|repair/.test(text)) return ["Emergency repair job", "Technician route gap", "Parts reorder watch", "Customer update draft", "Photo evidence", "Repeat service issue"];
  if (/legal|case|matter|filing/.test(text)) return ["Motion filing deadline", "Evidence packet", "Client update", "Matter intake", "Discovery follow-up", "Retainer document"];
  if (/dental|clinic|patient|appointment|treatment/.test(text)) return ["Implant consult follow-up", "Recall list gap", "Treatment plan approval", "Insurance pre-auth", "No-show recovery", "Lab case"];
  if (/construction|project|site|subcontractor/.test(text)) return ["Change order review", "Site blocker", "Subcontractor handoff", "Budget variance", "Photo evidence", "RFI response"];
  if (/restaurant|reservation|table|kitchen|hospitality|short-stay|guest/.test(text)) return ["Dinner rush staffing", "Guest issue recovery", "Inventory prep gap", "Turnover checklist", "Review response", "Booking follow-up"];
  if (/nonprofit|fundraising|donor|grant/.test(text)) return ["Major donor follow-up", "Grant deadline watch", "Pledge receipt", "Campaign task", "Board report metric", "Sponsor outreach"];
  if (/insurance|policy|claim|renewal/.test(text)) return ["Policy renewal watch", "Claim evidence packet", "Client risk note", "Carrier follow-up", "Document request", "Compliance receipt"];
  if (/logistics|dispatch|shipment|route|delivery/.test(text)) return ["Late shipment exception", "Carrier update", "Route risk", "Delivery document", "Customer notice", "Warehouse handoff"];
  if (/fitness|coaching|client|session|program/.test(text)) return ["Client check-in risk", "Program update", "Payment follow-up", "Progress review", "Session reminder", "Retention action"];
  if (/customer success|account|ticket|renewal|churn/.test(text)) return ["Renewal risk account", "Usage drop alert", "Ticket cluster", "Expansion signal", "Success plan", "Executive update"];
  if (/auto|dealer|vehicle|inventory/.test(text)) return ["SUV test drive lead", "Finance document gap", "Trade-in appraisal", "Inventory aging watch", "Service follow-up", "Delivery checklist"];
  if (/education|cohort|student|class/.test(text)) return ["At-risk student", "Attendance gap", "Assignment backlog", "Parent update", "Cohort progress", "Intervention plan"];
  if (/event|production|run of show|vendor/.test(text)) return ["Run-of-show change", "Vendor confirmation", "Budget approval", "Asset delivery", "Client sign-off", "Day-of issue"];
  if (/security|guard|incident|patrol/.test(text)) return ["Incident report", "Shift coverage gap", "Patrol evidence", "Client report", "Guard assignment", "Escalation watch"];
  if (/finance|dealflow|deal|fund|payment|invoice|cash/.test(text)) return ["Qualified target review", "IC memo draft", "Payment risk", "Invoice follow-up", "Cash forecast variance", "Portfolio update"];

  return [`${noun} source review`, `${noun} follow-up`, `${noun} approval`, `${noun} data gap`, `${noun} next action`, `${noun} receipt`];
}

function nextActionExamplesForDemo(demo: DemoData, stages: string[]): string[] {
  const text = demoSearchText(demo);

  if (/football|academy|player|scout|squad/.test(text)) return ["Ask where scouting notes live", "Rank fit by club need", "Queue trial follow-up", "Prepare decision packet", "Attach contract evidence", "Create receipt for next move"];
  if (/social media|creator|content|campaign|creative|brand|design/.test(text)) return ["Find the asset source", "Route to the right creative model", "Send approval card", "Publish after decision", "Attach performance result", "Recommend next campaign skill"];
  if (/oil|gas|energy|well|hse|permit|downtime/.test(text)) return ["Confirm field data source", "Flag risk evidence", "Request HSE permission", "Escalate maintenance decision", "Attach downtime receipt", "Recommend monitoring screen"];
  if (/\brealty\b|\breal estate\b|\blistings?\b|\bviewings?\b|\bbuyers?\b|\bsellers?\b|\bmortgage\b|property management|maintenance|lease|tenant/.test(text)) return ["Confirm source folder or CRM", "Prepare follow-up", "Request document permission", "Move to next stage", "Log owner/client receipt", "Recommend stale-deal screen"];

  return [
    `Find approved source for ${demo.vocabulary.tracks}`,
    stages[1] ? `Move to ${stages[1]}` : "Move work forward",
    "Send approval card",
    "Create receipt and close loop",
    "Show missing data",
    "Recommend next screen",
  ];
}

function likelySourceForDemo(demo: DemoData): string {
  if (demo.playbook?.common_integrations?.[0]) return demo.playbook.common_integrations[0];
  const text = demoSearchText(demo);

  if (/social media|creator|content|campaign/.test(text)) return "approved content calendar and asset folder";
  if (/oil|gas|energy|well|hse/.test(text)) return "approved inspection files and HSE folder";
  if (/ecommerce|sku|inventory|order/.test(text)) return "approved store export and order dashboard";
  if (/recruit|candidate|interview/.test(text)) return "approved ATS export and resume folder";
  if (/legal|case|matter/.test(text)) return "approved matter folder";
  if (/dental|clinic|patient/.test(text)) return "approved appointment export";
  if (/construction|site|project/.test(text)) return "approved project folder";
  if (/restaurant|hospitality|guest|booking/.test(text)) return "approved bookings and operations sheet";
  if (/finance|deal|invoice|cash|mortgage/.test(text)) return "approved deal and finance workspace";
  return "approved workspace source";
}

type SpecialistAgentProfile = {
  name: string;
  role: string;
  specialty: string;
};

function specialistAgentProfileForDemo(demo: DemoData): SpecialistAgentProfile {
  const text = `${demo.id} ${demo.name} ${demo.category} ${demo.vocabulary.tracks} ${demo.vocabulary.stages}`.toLowerCase();

  if (/football|academy|player|scout|squad/.test(text)) {
    return { name: "Scouting & Development Agent", role: "Football Operations", specialty: "scouting evidence, player movement, contracts, and development risk" };
  }
  if (/property management|maintenance|lease|tenant/.test(text)) {
    return { name: "Property Operations Agent", role: "Property Management", specialty: "vacancy, maintenance, lease renewals, documents, and owner follow-ups" };
  }
  if (/recruit|candidate|interview|hiring/.test(text)) {
    return { name: "Talent Pipeline Agent", role: "Recruiting", specialty: "candidates, interviews, scorecards, offers, and hiring evidence" };
  }
  if (/\brealty\b|\breal estate\b|\blistings?\b|\bviewings?\b|\bbuyers?\b|\bsellers?\b|\bmortgage\b/.test(text)) {
    return { name: "Real Estate Operator", role: "Real Estate", specialty: "listings, viewings, offers, documents, and buyer or seller next actions" };
  }
  if (/social media|creator|content|campaign|creative|brand|design/.test(text)) {
    return { name: "Campaign Production Agent", role: "Creative Operations", specialty: "briefs, brand rules, image/video model routing, approvals, and performance loops" };
  }
  if (/oil|gas|energy|well|hse|permit|downtime/.test(text)) {
    return { name: "Field Operations Analyst", role: "Energy Operations", specialty: "wells, maintenance, HSE evidence, downtime, and field handoffs" };
  }
  if (/trade|customs|shipment|docs|documents/.test(text)) {
    return { name: "Trade Documentation Agent", role: "Trade", specialty: "orders, shipment documents, customs holds, evidence, and broker handoffs" };
  }
  if (/manufacturing|quality|batch|defect|production/.test(text)) {
    return { name: "Quality Operations Agent", role: "Manufacturing", specialty: "batches, defects, inspections, production runs, and corrective actions" };
  }
  if (/ecommerce|order|cart|sku|inventory/.test(text)) {
    return { name: "Commerce Operations Agent", role: "Ecommerce", specialty: "orders, products, inventory, fulfillment, customer issues, and revenue signals" };
  }
  if (/field service|service ticket|dispatch|repair/.test(text)) {
    return { name: "Dispatch Operations Agent", role: "Field Service", specialty: "jobs, technicians, routes, parts, repairs, and service follow-ups" };
  }
  if (/legal|case|matter|filing/.test(text)) {
    return { name: "Case Operations Agent", role: "Legal", specialty: "cases, filings, evidence, deadlines, and client follow-ups" };
  }
  if (/dental|clinic|patient|appointment|treatment/.test(text)) {
    return { name: "Clinic Flow Agent", role: "Healthcare", specialty: "patients, appointments, treatment plans, recalls, and documentation gaps" };
  }
  if (/construction|project|site|subcontractor/.test(text)) {
    return { name: "Project Delivery Agent", role: "Construction", specialty: "projects, site tasks, blockers, subcontractors, RFIs, and handoffs" };
  }
  if (/restaurant|reservation|table|kitchen|hospitality|short-stay|guest/.test(text)) {
    return { name: "Hospitality Operations Agent", role: "Hospitality", specialty: "reservations, guests, service issues, shifts, inventory, and reviews" };
  }
  if (/nonprofit|fundraising|donor|grant/.test(text)) {
    return { name: "Fundraising Pipeline Agent", role: "Nonprofit", specialty: "donors, grants, pledges, campaigns, board reporting, and follow-ups" };
  }
  if (/insurance|policy|claim|renewal/.test(text)) {
    return { name: "Policy & Claims Agent", role: "Insurance", specialty: "policies, claims, renewals, risk notes, and client follow-ups" };
  }
  if (/logistics|dispatch|shipment|route|delivery/.test(text)) {
    return { name: "Logistics Control Agent", role: "Logistics", specialty: "shipments, routes, exceptions, documents, and delivery handoffs" };
  }
  if (/fitness|coaching|client|session|program/.test(text)) {
    return { name: "Coaching Progress Agent", role: "Wellness", specialty: "clients, sessions, progress, programs, check-ins, and retention risks" };
  }
  if (/customer success|account|ticket|renewal|churn/.test(text)) {
    return { name: "Customer Success Agent", role: "Customer Success", specialty: "accounts, tickets, renewals, churn risk, and success plans" };
  }
  if (/auto|dealer|vehicle|inventory/.test(text)) {
    return { name: "Vehicle Pipeline Agent", role: "Automotive", specialty: "vehicles, leads, test drives, finance documents, and delivery steps" };
  }
  if (/education|cohort|student|class/.test(text)) {
    return { name: "Cohort Progress Agent", role: "Education", specialty: "students, cohorts, lessons, assignments, attendance, and intervention signals" };
  }
  if (/event|production|run of show|vendor/.test(text)) {
    return { name: "Event Production Agent", role: "Events", specialty: "vendors, schedules, run-of-show, approvals, and production risks" };
  }
  if (/security|guard|incident|patrol/.test(text)) {
    return { name: "Security Operations Agent", role: "Security", specialty: "guards, shifts, patrols, incidents, evidence, and escalation paths" };
  }
  if (/finance|dealflow|deal|fund|payment|invoice|cash/.test(text)) {
    return { name: "Finance Control Agent", role: "Finance", specialty: "deals, invoices, cash, payments, forecasts, and approval receipts" };
  }
  return { name: "Workflow Research Agent", role: demo.category, specialty: `researching ${demo.category.toLowerCase()} workflows, source systems, skill gaps, and safe next screens` };
}

export function buildSpecialistAgent(demo: DemoData): DemoShellAgent {
  const profile = specialistAgentProfileForDemo(demo);

  return {
    name: profile.name,
    role: profile.role,
    status: "Recommended",
    summary: `For ${demo.category.toLowerCase()}: watches ${demo.vocabulary.tracks} and ${demo.vocabulary.worksWith}, handles ${profile.specialty}, and turns repeated work into Decisions.`,
  };
}

export function buildDemoShellAgents(demo: DemoData): DemoShellAgent[] {
  return [
    {
      name: "The Operator",
      role: "Worker",
      status: "Active",
      summary: `Moves ${demo.vocabulary.tracks} forward, creates records, and asks where live data lives when context is missing.`,
    },
    {
      name: "The Analyst",
      role: "Advisor",
      status: "Active",
      summary: `Reads the workspace, finds risk signals, and turns ${demo.vocabulary.tracks} into useful answers, tables, and charts.`,
    },
    {
      name: "The Architect",
      role: "System",
      status: "System",
      summary: "Proposes new screens, skills, integrations, and upgrades only when the Operating System can explain why they matter.",
    },
    buildSpecialistAgent(demo),
  ];
}

export function buildDemoShellRecords(demo: DemoData): DemoShellRecord[] {
  const stages = splitDemoStages(demo);
  const parties = splitDemoCounterparties(demo);
  const noun = titleCase(singularDemoNoun(demo.vocabulary.tracks));
  const titles = recordTitleExamplesForDemo(demo, noun);
  const nextActions = nextActionExamplesForDemo(demo, stages);

  return stages.slice(0, 6).map((stage, index) => {
    const owner = parties[index % Math.max(parties.length, 1)] || "operator";

    return {
      id: `${demo.id}-${index + 1}`,
      title: titles[index % titles.length],
      stage,
      owner: titleCase(owner),
      nextAction: nextActions[index % nextActions.length],
      signal:
        index % 3 === 0
          ? "Needs source"
          : index % 3 === 1
            ? "Agent ready"
            : "Decision queued",
    };
  });
}

export function buildDemoPermissionReceipt(demo: DemoData): DemoShellReceipt {
  const source = likelySourceForDemo(demo);

  return {
    id: `${demo.id}-desktop-permission`,
    title: "Desktop Agent permission granted",
    detail: `Connected ${source} so agents can read the approved source for ${demo.vocabulary.tracks}.`,
    assistant: "The Operator",
    status: "Verified",
  };
}

export function buildDemoRecordActionReceipt(demo: DemoData, record: DemoShellRecord): DemoShellReceipt {
  return {
    id: `${record.id}-action`,
    title: record.nextAction,
    detail: `${record.title} moved through ${demo.vocabulary.stages} with ${record.owner} context attached.`,
    assistant: "The Operator",
    status: "Demo",
  };
}

export function buildDemoShellReceipts(demo: DemoData, records: DemoShellRecord[]): DemoShellReceipt[] {
  return records.slice(0, 3).map((record, index) => ({
    id: `${record.id}-receipt`,
    title: index === 0 ? `Mapped ${demo.vocabulary.tracks} source` : record.nextAction,
    detail: `${record.title} - ${record.owner}. This is the kind of audit trail the live Operating System keeps as agents work.`,
    assistant: index === 1 ? "The Analyst" : "The Operator",
    status: "Demo",
  }));
}

export function buildDemoShellScreens(demo: DemoData): string[] {
  return ["Mission Control", ...demo.modules, "Agent Library", "Integrations", "Decisions", "Receipts", "Weekly Review"];
}
